import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import axios from 'axios';

// @mui
import { styled } from '@mui/material/styles';
import {
  Button,
  Link,
  Stack,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Card,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import Page from '../../components/Page';

import { login } from '../../redux/actions/authActions';
import pic from './mail2.png';

// ----------------------------------------------------------------------

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// عداد لإعادة الإرسال مع مربع تعديل البريد بعد انتهاء الوقت
function CountdownTimer({ minutes, onExpire, sentEmail }) {
  const [secondsLeft, setSecondsLeft] = useState(minutes * 60);
  const [editableEmail, setEditableEmail] = useState(sentEmail);

  useEffect(() => {
    const lastSent = localStorage.getItem("lastResetEmailSent");
    if (lastSent) {
      const diff = Date.now() - parseInt(lastSent, 10);
      const remaining = minutes * 60 * 1000 - diff;
      if (remaining > 0) {
        setSecondsLeft(Math.floor(remaining / 1000));
      } else {
        setSecondsLeft(0);
      }
    }

    if (secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const minutesLeft = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  if (secondsLeft > 0) {
    return (
      <Button variant="outlined" disabled>
        إعادة الإرسال متاحة بعد {minutesLeft}:{seconds < 10 ? `0${seconds}` : seconds}
      </Button>
    );
  }

  // بعد انتهاء العداد → مربع تعديل البريد + تحذير + زر إعادة الإرسال
  return (
    <Box>
      <TextField
        fullWidth
        type="email"
        label="Your email"
        value={editableEmail}
        onChange={(e) => setEditableEmail(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Typography sx={{ color: 'error.main', mb: 2, fontSize: '0.9rem' }}>
        ⚠️ من فضلك راجع البريد جيدًا قبل إعادة الإرسال  
        <br /> ⚠️ Please double-check your email before resending
      </Typography>
      <Button
        variant="contained"
        onClick={() => {
          if (editableEmail) {
            localStorage.setItem("lastResetEmailSent", Date.now().toString());
            localStorage.setItem("lastResetEmailAddress", editableEmail);
            onExpire(editableEmail);
          }
        }}
      >
        إعادة إرسال البريد الإلكتروني
      </Button>
    </Box>
  );
}

export default function ResetPass() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userLogin);
  const { error: loginError, loading: loginLoading, userInfo } = userLogin;

  const [res, setRes] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const MailSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: MailSchema,
    onSubmit: () => {
      dispatch(login(values.email));
    },
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  const resetHandler = (overrideEmail = null) => {
    const url = `${process.env.REACT_APP_API_KEY}/api/v1/accounts/send-reset-password-email/`;
    const email = overrideEmail || values.email;

    try {
      axios.post(url, { email }).then((response) => console.log(response));
      setRes(true);
      setSentEmail(email);
      localStorage.setItem("lastResetEmailSent", Date.now().toString());
      localStorage.setItem("lastResetEmailAddress", email);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (userInfo) {
      navigate('/', { replace: true });
    }

    // لو فيه إرسال سابق مخزن في LocalStorage
    const lastSent = localStorage.getItem("lastResetEmailSent");
    const lastEmail = localStorage.getItem("lastResetEmailAddress");
    if (lastSent && lastEmail) {
      const diff = Date.now() - parseInt(lastSent, 10);
      const remaining = 3 * 60 * 1000 - diff;
      if (remaining > 0) {
        setRes(true);
        setSentEmail(lastEmail);
      }
    }
  }, [navigate, userInfo]);

  return (
    <Page title="Reset Password">
      {res === false ? (
        <FormikProvider value={formik}>
          <ContentStyle>
            <Typography variant="h2" gutterBottom>
              Reset Password
            </Typography>

            <Typography sx={{ color: 'text.secondary', mb: 5 }}>
              Enter your Email below.
            </Typography>

            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3} paddingBottom={'3rem'}>
                <TextField
                  fullWidth
                  autoComplete="username"
                  type="email"
                  label="Email address"
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Stack>

              {loginError ? (
                <Alert severity="error">
                  <AlertTitle>Login Error</AlertTitle>
                  {loginError}
                </Alert>
              ) : null}

              <Button
                onClick={() => resetHandler()}
                fullWidth
                size="large"
                variant="contained"
              >
                Reset my password
              </Button>
              <Typography variant="body2" paddingTop={'2rem'}>
                Don’t have an account?{' '}
                <Link variant="subtitle2" component={RouterLink} to="/register">
                  Get started
                </Link>
              </Typography>
            </Form>
          </ContentStyle>
        </FormikProvider>
      ) : (
        <ContentStyle sx={{ textAlign: 'center', alignItems: 'center' }}>
          <Card sx={{ p: 4, boxShadow: 3 }}>
            <Box sx={{ maxWidth: '250px', mx: 'auto', mb: 2 }}>
              <img src={pic} alt="Mail sent" style={{ width: '100%' }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              Check your inbox
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 3 }}>
              ✅ لقد أرسلنا رابط إعادة تعيين كلمة المرور إلى البريد الإلكتروني: 
              <strong> {sentEmail} </strong> <br />
              قد تجد الرسالة في مجلد الرسائل غير المرغوب فيها (Spam). من فضلك تحقق من جميع صناديق البريد.
              <br /><br />
              ✅ We have sent a reset password link to: 
              <strong> {sentEmail} </strong> <br />
              Please check your inbox and also the Spam/Junk folder.
            </Typography>
            <CountdownTimer minutes={3} onExpire={resetHandler} sentEmail={sentEmail} />
          </Card>
        </ContentStyle>
      )}
    </Page>
  );
}
